import { Alert, Button, Card, Flex, Form, Input, Typography } from "antd";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { ApiError, api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { safeNext } from "@/auth/guards";
import { AuthLayout } from "@/components/AuthLayout";
import { useI18n } from "@/i18n";

type Values = { email: string; password: string };

// What shipped lately, for people signing back in. Update with releases; only features live on go.zerpid.com.
const NEWS = ["auth.login.new1", "auth.login.new2", "auth.login.new3"] as const;

export function Login() {
  const { t } = useI18n();
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const errorText = useErrorText();
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<Values>();

  const submit = async (values: Values) => {
    setSubmitting(true);
    setError(null);
    try {
      const session = await unwrap(api.POST("/v1/auth/login", { body: values }));
      await signIn(session);
      navigate(safeNext(params.get("next")), { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const unverified = error instanceof ApiError && error.code === "email_unverified";

  return (
    <AuthLayout
      title={t("auth.login.title")}
      aside={
        <section style={{ width: "100%", maxWidth: 360 }}>
          <Typography.Text type="secondary" strong style={{ fontSize: 12, letterSpacing: ".08em", textTransform: "uppercase" }}>
            {t("auth.login.newEyebrow")}
          </Typography.Text>
          <Typography.Title level={4} style={{ marginTop: 4 }}>
            {t("auth.login.newTitle")}
          </Typography.Title>
          <ul style={{ margin: "0 0 24px", paddingLeft: 18, display: "flex", flexDirection: "column", gap: 10 }}>
            {NEWS.map((key) => (
              <li key={key}>
                <Typography.Text>{t(key)}</Typography.Text>
              </li>
            ))}
          </ul>
          <Card size="small" styles={{ body: { padding: 16 } }}>
            <Flex vertical gap={10} align="flex-start">
              <Typography.Text strong>{t("auth.login.promoTitle")}</Typography.Text>
              <Typography.Text type="secondary">{t("auth.login.promoBody")}</Typography.Text>
              <Button onClick={() => navigate("/register")}>{t("auth.login.promoCta")}</Button>
            </Flex>
          </Card>
          <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0, fontSize: 13 }}>
            {t("auth.login.help")} <a href="mailto:halo@zerpid.com">halo@zerpid.com</a>
          </Typography.Paragraph>
        </section>
      }
    >
      {error !== null && (
        <Alert
          type={unverified ? "warning" : "error"}
          showIcon
          style={{ marginBottom: 16 }}
          title={errorText(error)}
          description={
            unverified ? (
              <>
                {t("auth.login.unverifiedHelp")}{" "}
                <Link to={`/verify?email=${encodeURIComponent(form.getFieldValue("email") ?? "")}`}>
                  {t("auth.login.resend")}
                </Link>
              </>
            ) : undefined
          }
        />
      )}
      <Form form={form} layout="vertical" onFinish={submit} requiredMark={false} disabled={submitting}>
        <Form.Item
          name="email"
          label={t("common.email")}
          rules={[{ required: true, type: "email", message: t("field.invalid_email") }]}
        >
          <Input autoComplete="email" autoFocus inputMode="email" />
        </Form.Item>
        <Form.Item
          name="password"
          label={t("common.password")}
          rules={[{ required: true, message: t("field.required") }]}
          extra={<Link to="/forgot-password">{t("auth.login.forgot")}</Link>}
        >
          <Input.Password autoComplete="current-password" />
        </Form.Item>
        <Button type="primary" htmlType="submit" block loading={submitting}>
          {t("auth.login.submit")}
        </Button>
      </Form>
      <Typography.Paragraph style={{ marginTop: 24, marginBottom: 0, textAlign: "center" }}>
        {t("auth.login.noAccount")} <Link to="/register">{t("auth.login.register")}</Link>
      </Typography.Paragraph>
    </AuthLayout>
  );
}
